import { AlertTriangle } from "lucide-react";
import { IntensityIndicator } from "@/components/shared/IntensityIndicator";
import { StatusBadge } from "@/components/ui/status-badge";
import type { StaffingRequestRow } from "@/hooks/use-staffing-requests";
import { StaffingStatusBadge } from "./StaffingStatusBadge";

const SOURCE_LABELS: Record<string, string> = {
  SOW: "SOW",
  ResourcePlan: "Resource Plan",
  ExternalHook: "External Hook",
  Template: "Template",
};

interface Props {
  request: StaffingRequestRow;
  onClick: (id: string) => void;
}

export function RequestQueueRow({ request, onClick }: Props) {
  const receivedDate = new Date(request.createdAt).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <tr
      onClick={() => onClick(request.id)}
      className="cursor-pointer border-t border-border hover:bg-muted/40"
    >
      <td className="px-3 py-2.5">
        <div className="font-medium text-foreground">
          {(() => {
            // Error uploads never parsed a title — show the filename instead.
            const title = request.title ?? request.originalFilename ?? "Untitled upload";
            return title.length > 40 ? `${title.slice(0, 40)}…` : title;
          })()}
        </div>
        <div className="text-[10px] text-muted-foreground">
          {SOURCE_LABELS[request.sourceType] ?? request.sourceType}
        </div>
      </td>

      <td className="px-3 py-2.5 text-muted-foreground">
        {request.accountName ?? <span className="italic text-muted-foreground/50">Unknown</span>}
      </td>

      <td className="px-3 py-2.5">
        {request.isUnlinked ? (
          <StatusBadge
            status="pending"
            showDot={false}
            className="h-5 gap-1 px-1.5 text-[10px] font-semibold"
          >
            <AlertTriangle className="h-3 w-3" />
            Unlinked
          </StatusBadge>
        ) : (
          <span className="text-muted-foreground">
            {request.solutionName ?? request.linkedSolution?.name ?? "—"}
          </span>
        )}
      </td>

      <td className="px-3 py-2.5">
        <div className="font-medium text-foreground">{request.lineCount}</div>
        {request.unmappedCount > 0 && (
          <div className="text-[10px] text-warning-600 dark:text-warning-400">
            {request.unmappedCount} unmapped
          </div>
        )}
      </td>

      <td className="px-3 py-2.5">
        {request.suggestedIntensity !== null ? (
          <div
            className="flex items-center gap-1"
            title={`Suggested Intensity: ${request.suggestedIntensity}`}
          >
            <IntensityIndicator level={request.suggestedIntensity} />
            <span className="text-[10px] text-muted-foreground">{request.suggestedIntensity}</span>
          </div>
        ) : (
          <span className="text-[10px] text-muted-foreground">—</span>
        )}
      </td>

      <td className="px-3 py-2.5">
        <StaffingStatusBadge status={request.status} />
      </td>

      <td className="px-3 py-2.5 text-[11px] text-muted-foreground">{receivedDate}</td>
    </tr>
  );
}
